import { Injectable, Optional } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { CacheService } from '../cache';
import { ResourceService } from './resource';
import { Notification } from '../../models/notification';
import { io, Socket } from 'socket.io-client';
import { EnvironmentConfig } from '../config';

@Injectable({
    providedIn: 'root'
})
export class NotificationService extends ResourceService<Notification> {
    private socket: Socket | undefined;
    private newNotificationSubject = new Subject<any>();
    public newNotification$ = this.newNotificationSubject.asObservable();

    constructor(
        protected http: HttpClient,
        protected cache: CacheService,
        @Optional() private config: EnvironmentConfig
    ) {
        super(http, cache, 'notifications');
    }

    /**
     * Initializes the socket connection for notifications.
     * Connects to the server, authenticates with the provided user ID,
     * and listens for the 'new_notification' event.
     */
    public initSocket(userId: string) {
        if (this.socket) {
            this.socket.disconnect();
        }

        // Uses socketUrl or api from the environment configuration
        const url = new URL(this.config.api);

        this.socket = io(url.origin, {
            transports: ['websocket']
        });

        this.socket.on('connect', () => {
            this.socket?.emit('authenticate', userId);
        });

        this.socket.on('new_notification', (data) => {
            this.newNotificationSubject.next(data);
        });
    }

    /**
     * Disconnects the socket.
     */
    public disconnectSocket() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = undefined;
        }
    }

    /**
     * 1. GET {domain}/notifications?page=1&limit=20
     */
    public getNotifications(page: number = 1, limit: number = 20, isRead?: boolean): Observable<Notification[]> {
        let params = new HttpParams()
            .set('page', page.toString())
            .set('limit', limit.toString());

        if (isRead !== undefined) {
            params = params.set('isRead', isRead.toString());
        }

        return this.fetch(params);
    }

    /**
     * 2. GET {domain}/notifications/unread-count
     */
    public getUnreadCount(): Observable<any> {
        return this.http.get(`${this.endpoint}/unread-count`);
    }

    /**
     * 3. GET {domain}/notifications/:id
     */
    public getNotificationById(id: string): Observable<Notification> {
        return this.get(id);
    }

    /**
     * 4. PUT {domain}/notifications/:id/read
     */
    public markAsRead(id: string): Observable<any> {
        return this.http.put(`${this.endpoint}/${id}/read`, {});
    }

    /**
     * 5. PUT {domain}/notifications/read-all
     */
    public markAllAsRead(): Observable<any> {
        return this.http.put(`${this.endpoint}/read-all`, {});
    }

}
